import React, { useState, useEffect, useCallback, useRef } from "react";
import CustomAudienceModal from "./CustomAudienceModal";
import CreateAudienceModal from "../Ads/CreateAudienceModal";
import axiosInstance from "../../API/axiosInstance";
import { useNavigate } from "react-router-dom";
import { toastify } from "../../components/Toast";
import "react-toastify/dist/ReactToastify.css";
import CreateCampaignModal from "../EmailAutomation/CreateCampaign";
import RichDescription from "../RichDescription/RichDescription";
import "./GenerateContentModal.scss";
import {
  Megaphone,
  Share2,
  Mail,
  FileText,
  Layers,
  Briefcase,
  GraduationCap,
  Heart,
  Gamepad2,
  Palette,
  Utensils,
  Plus,
  Minus,
  Upload,
  Zap,
  Users,
  ChevronDown,
  X,
  Search,
  Check,
  Video
} from "lucide-react";

function extractReferenceText(html) {
  if (!html) return "";
  const documentNode = new DOMParser().parseFromString(html, "text/html");
  return documentNode.body?.textContent || "";
}

export const getIconComponent = (iconName) => {
  const Icon = {
    Megaphone,
    Share2,
    Mail,
    FileText,
    Layers,
    Briefcase,
    GraduationCap,
    Heart,
    Gamepad2,
    Palette,
    Utensils,
    Plus,
    Minus,
    Upload,
    Zap,
    Users,
    ChevronDown,
    X,
    Search,
    Check,
    Video
  }[iconName] || Users;

  return <Icon className="w-5 h-5" />;
};

export const videoCategories = [
  {
    id: "speaking-character",
    title: "Speaking Character",
    description: "Create an AI character that speaks your script",
    icon: "Users",
    path: "/app/speaking-character",
  },
  {
    id: "talking-head",
    title: "Talking Head",
    description: "Create a realistic talking head video",
    icon: "Megaphone",
    path: "/app/talking-head",
  },
];



export const AspectRatioIcon = ({ ratio }) => {
  switch (ratio) {
    case "1:1":
    case "1024x1024":
      return (
        <svg
          className="w-4 h-4 text-gray-500"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="3" y="3" width="10" height="10" rx="1" />
        </svg>
      );
    case "9:16":
    case "1024x1536":
      return (
        <svg
          className="w-4 h-4 text-gray-500"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="5" y="2" width="6" height="12" rx="1" />
        </svg>
      );
    case "16:9":
    case "1536x1024":
      return (
        <svg
          className="w-4 h-4 text-gray-500"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="2" y="5" width="12" height="6" rx="1" />
        </svg>
      );
    case "4:5":
      return (
        <svg
          className="w-4 h-4 text-gray-500"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="4" y="2" width="8" height="10" rx="1" />
        </svg>
      );
    default:
      return null;
  }
};
const sizeOptions = [
  { id: "1:1", label: "Square (1:1)", value: "1:1" },
  { id: "9:16", label: "Portrait (9:16)", value: "9:16" },
  { id: "16:9", label: "Landscape (16:9)", value: "16:9" },
  { id: "4:5", label: "Portrait (4:5)", value: "4:5" },
];

export const contentTypes = [
  {
    id: "ads",
    title: "Ads",
    description: "Social media advertisements and promotional content",
    icon: "Megaphone",
    color: "bg-orange-50 border-orange-200 hover:bg-orange-100",
  },
  {
    id: "social",
    title: "Social Media",
    description: "Posts, stories, and social content",
    icon: "Share2",
    color: "bg-blue-50 border-blue-200 hover:bg-blue-100",
  },
  {
    id: "emails",
    title: "Emails",
    description: "Email campaigns and newsletters",
    icon: "Mail",
    color: "bg-green-50 border-green-200 hover:bg-green-100",
  },
  {
    id: "landing",
    title: "Landing Page Copy",
    description: "Website copy and landing page content",
    icon: "FileText",
    color: "bg-purple-50 border-purple-200 hover:bg-purple-100",
  },
  {
    id: "stacked-ads",
    title: "Stacked Ads",
    description: "Multiple ad variations using 7-10 proven frameworks",
    icon: "Layers",
    color: "bg-rose-50 border-rose-200 hover:bg-rose-100",
  },
  {
    id: "blog",
    title: "Blog",
    icon: "FileText",
    color: "bg-indigo-50 border-indigo-200 hover:bg-indigo-100",
  },
  {
    id: "videos",
    title: "Videos",
    description: "Create AI-powered videos with speaking characters",
    icon: "Video",
    color: "bg-amber-50 border-amber-200 hover:bg-amber-100",
  },
];

const GenerateContentModal = ({
  open,
  onOpenChange,
  selectedBrandId,
  selectedBrandName,
  selectedAudienceId,
  selectedAudienceName,
  setInProcessAdWithReference,
  onContentGenerated,
  preselectedContentType,
  referenceCard,
  referenceCardId,
  onHandleBrandSelect,
  selectedBrandPage,
  isBrandDetailPage = false,
  isEmbedded = false,
  onBackToContentTypes,
}) => {
  const navigate = useNavigate();
  const [selectedContentType, setSelectedContentType] = useState(isEmbedded ? "ads" : "");
  const [selectedAudience, setSelectedAudience] = useState("");
  const [selectedBrand, setSelectedBrand] = useState(selectedBrandId || "");
  const [prompt, setPrompt] = useState("");
  const [adsCount, setAdsCount] = useState(5);
  const [referenceImage, setReferenceImage] = useState(null);
  const [step, setStep] = useState(isEmbedded ? 2 : 1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  // const [selectedSize, setSelectedSize] = useState("1024x1024");
  const [showCustomAudienceModal, setShowCustomAudienceModal] = useState(false);
  const [customAudiences, setCustomAudiences] = useState([]);
  const [brands, setBrands] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [editingAudience, setEditingAudience] = useState(null);
  const [, setApiError] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showOriginalReference, setShowOriginalReference] = useState(false);
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [brandSearchTerm, setBrandSearchTerm] = useState("");
  const [localBrandName, setLocalBrandName] = useState(
    selectedBrandName || "Please select a brand"
  );
  const [showCreateCampaingModal, setShowCreateCampaingModal] = useState(false);
  // Add this state variable with your existing state
  const [isReferenceExpanded, setIsReferenceExpanded] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState("1:1");
  const aspectRatios = [
    { id: "1:1", value: "1:1", label: "Square (1:1)" },
    { id: "9:16", value: "9:16", label: "Vertical (9:16)" },
    { id: "16:9", value: "16:9", label: "Wide (16:9)" },
    { id: "4:5", value: "4:5", label: "Portrait (4:5)" },
  ];

  const [isDragOver, setIsDragOver] = useState(false);
  const dropZoneRef = useRef(null);
  const dropdownRef = useRef(null);

  const handleAspectRatioChange = (ratio) => {
    setSelectedSize(ratio);
    setIsDropdownOpen(false);
  };

  // Validate and process image file
  const processImageFile = (file) => {
    if (!file) return false;

    // Check if it's an image
    if (!file.type.startsWith("image/")) {
      toastify("error", "Please select a valid image file");
      return false;
    }

    // Check file size (optional - adjust as needed, e.g., 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toastify("error", "Image file size should be less than 10MB");
      return false;
    }

    setReferenceImage(file);
    return true;
  };

  // Handle drag events
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set drag over to false if we're leaving the drop zone entirely
    if (!dropZoneRef.current?.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      processImageFile(file);
    }
  }, []);

  // Handle paste events
  useEffect(() => {
    const handlePaste = (e) => {
      // Only handle paste when the modal is open and we're on step 3
      if (!open || step < 3) return;

      const items = Array.from(e.clipboardData.items);
      const imageItem = items.find((item) => item.type.startsWith("image/"));

      if (imageItem) {
        e.preventDefault();
        const file = imageItem.getAsFile();
        if (file) {
          processImageFile(file);
        }
      }
    };

    if (open && step >= 3) {
      document.addEventListener("paste", handlePaste);
      return () => document.removeEventListener("paste", handlePaste);
    }
  }, [open, step]);

  // Update selected brand when prop changes
  useEffect(() => {
    if (selectedBrandId) {
      setSelectedBrand(selectedBrandId);
    }
  }, [selectedBrandId]);

  // Update selected audience when prop changes
  useEffect(() => {
    if (selectedAudienceId) {
      setSelectedAudience(selectedAudienceId);
    }
  }, [selectedAudienceId]);

  // Reset form when modal closes, but preserve brand and audience selection
  useEffect(() => {
    if (!open) {
      setSelectedContentType("");
      setSelectedAudience("");
      setPrompt("");
      setAdsCount(5);
      setReferenceImage(null);
      setStep(1);
      setIsGenerating(false);
      setSelectedSize("1:1");
      setIsDragOver(false);
    } else {
      if (selectedBrandId) {
        setSelectedBrand(selectedBrandId);
      }
      if (selectedAudienceId) {
        setSelectedAudience(selectedAudienceId);
      }
    }
  }, [open, selectedBrandId, selectedAudienceId]);

  useEffect(() => {
    setLocalBrandName(selectedBrandName || "Please select a brand");
  }, [selectedBrandName]);

  useEffect(() => {
    if (selectedBrandId) {
      setSelectedBrand(selectedBrandId);
    }
  }, [selectedBrandId]);

  useEffect(() => {
    if (open) {
      if (preselectedContentType) {
        setSelectedContentType(preselectedContentType);
        if (selectedAudienceId) {
          setStep(3);
        } else {
          setStep(2);
        }
      } else if (selectedAudienceId) {
        setStep(2);
      }

      if (selectedBrandId) {
        setSelectedBrand(selectedBrandId);
      }
      if (selectedAudienceId) {
        setSelectedAudience(selectedAudienceId);
      }
    }
  }, [preselectedContentType, selectedBrandId, selectedAudienceId, open]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    const controller = new AbortController();
    const token = localStorage.getItem("token");
    const fetchBrands = async () => {
      try {
        const response = await axiosInstance.get(`${process.env.REACT_APP_API}brands`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!active) return;
        const brandList = Array.isArray(response?.data?.data) ? response.data.data : [];
        setBrands(brandList);
        if (referenceCardId && !selectedBrandId && !selectedBrandName && brandList.length) {
          setSelectedBrand(brandList[0]._id);
          setLocalBrandName(brandList[0].brandName);
        }
      } catch (err) {
        if (!active || controller.signal.aborted) return;
        console.error(err, "err");
        setBrands([]);
      }
    };
    fetchBrands();
    return () => { active = false; controller.abort(); };
  }, [open, referenceCardId, selectedBrandId, selectedBrandName]);

  const filteredBrands = brands.filter(
    (brand) =>
      brand.brandName
        ?.toLowerCase()
        .includes(brandSearchTerm?.toLowerCase()) ||
      brand.description
        ?.toLowerCase()
        .includes(brandSearchTerm?.toLowerCase()) ||
      brand.industry?.toLowerCase().includes(brandSearchTerm?.toLowerCase())
  );

  useEffect(() => {
    const token = localStorage.getItem("token");
    const fetchAudiences = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API}audiences`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setAudiences(response.data.data);
      } catch (err) {
        setAudiences([]);
      }
    };
    fetchAudiences();
  }, [showCreateModal]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowBrandDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSaveAudience = (audienceData) => {
    if (editingAudience) {
      // Update existing audience
      setAudiences((prev) =>
        prev.map((audience) =>
          audience.id === editingAudience.id
            ? { ...audienceData, id: editingAudience.id }
            : audience
        )
      );
      setEditingAudience(null);
    } else {
      // Create new audience
      setAudiences((prev) => [
        ...prev,
        {
          ...audienceData,
          createdAt: new Date().toISOString().split("T")[0],
          usageCount: 0,
        },
      ]);
    }
    setShowCreateModal(false);
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setApiError("");
    const token = localStorage.getItem("token");
    try {
      const targetTab = selectedContentType === "videos" ? "video" : selectedContentType;
      if (referenceCardId) {
        setInProcessAdWithReference(true);
        const formData = {
          refrenceCardId: referenceCardId,
          type: capitalizeFirst(selectedContentType),
          userPrompt: prompt,
          size: selectedSize,
          quantity: adsCount,
          numberOfAds: adsCount,
          audienceId: selectedAudience ? selectedAudience : null,
        };
        const url = `${process.env.REACT_APP_API}ads/create-using-reference/${selectedBrand}`;
        await axiosInstance.post(url, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toastify(
          "success",
          <>
            <p>
              Generating…
              <br />
              Come back later if you’d like.
            </p>
          </>
        );
        setInProcessAdWithReference(false);
        setTimeout(() => {
          navigate("/app/ads", { state: { tab: targetTab, brandId: selectedBrand } });
        }, 1000);
      } else {
        const formData = new FormData();
        formData.append("type", capitalizeFirst(selectedContentType));
        if (selectedAudience) formData.append("audienceId", selectedAudience);
        formData.append("userPrompt", prompt);
        formData.append("numberOfAds", adsCount);
        formData.append("quantity", adsCount);
        formData.append("size", selectedSize);
        if (referenceImage) formData.append("referenceImage", referenceImage);

        const url = `${process.env.REACT_APP_API}ads/create/${selectedBrand}`;
        await axiosInstance.post(url, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        toastify(
          "success",
          <>
            <p>
              Generating…
              <br />
              Come back later if you’d like.
            </p>
          </>
        );
      }

      setIsGenerating(false);
      localStorage.setItem("selectedBrandId", selectedBrand);
      navigate("/app/ads", { state: { tab: targetTab, brandId: selectedBrand } });
      onOpenChange(false);
    } catch (err) {
      setIsGenerating(false);
      setApiError("Failed to create ad. Please try again.");
      toastify("error", err.response.data.message);
      const targetTab = selectedContentType === "videos" ? "video" : selectedContentType;
      setTimeout(() => {
        navigate("/app/ads", { state: { tab: targetTab } });
      }, 1000);
      console.error(err);
    }
  };

  const capitalizeFirst = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const handleSaveCustomAudience = (audience) => {
    setCustomAudiences((prev) => [...prev, audience]);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      processImageFile(file);
    }
  };

  const allAudiences = [...audiences, ...customAudiences];

  const handleContentTypeSelect = (typeId) => {
    setSelectedContentType(typeId);
    if (typeId === "videos") {
      setStep(1); // Stay on step 1 to show video options
    } else {
      setStep(2);
    }
    setForceUpdate((prev) => prev + 1);
  };

  const handleVideoCategorySelect = (category) => {
    onOpenChange(false);
    navigate(category.path);
  };



  if (!open && !isEmbedded) return null;

  return (
    <>
      <div
        className={isEmbedded ? "" : "fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]"}
        onClick={isEmbedded ? undefined : () => onOpenChange(false)}
      >
        <div
          className={isEmbedded ? "generate-content-modal w-full" : "generate-content-modal bg-white dark:bg-[#111111] rounded-3xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col"}
          onClick={isEmbedded ? undefined : (e) => e.stopPropagation()}
        >
          {!isEmbedded && (
            <div className="p-6 border-b border-gray-200 dark:border-white/10 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Generate Content
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">
                    Create personalized content for your brand and audience
                  </p>
                </div>
                <button
                  onClick={() => {
                    onOpenChange(false);
                    window.history.replaceState({}, document.title, "/app/ads");
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
            </div>
          )}

          <div className="p-6 space-y-8">
            {/* Progress Steps */}
            {!isEmbedded && (
              <div className="flex items-center justify-center space-x-4">
                <div
                  className={`flex items-center space-x-2 ${step >= 1
                    ? "text-orange-600"
                    : "text-gray-400 dark:text-gray-500"
                    }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step >= 1 ? "border-orange-600 bg-orange-50 dark:bg-orange-500/10" : "border-gray-200 dark:border-white/10"
                      }`}
                  >
                    1
                  </div>
                  <span className="font-medium hidden sm:block">Content Type</span>
                </div>
                <div className="w-12 h-0.5 bg-gray-200 dark:bg-white/10" />
                <div
                  className={`flex items-center space-x-2 ${step >= 2
                    ? "text-orange-600"
                    : "text-gray-400 dark:text-gray-500"
                    }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step >= 2 ? "border-orange-600 bg-orange-50 dark:bg-orange-500/10" : "border-gray-200 dark:border-white/10"
                      }`}
                  >
                    2
                  </div>
                  <span className="font-medium hidden sm:block">Audience</span>
                </div>
                <div className="w-12 h-0.5 bg-gray-200 dark:bg-white/10" />
                <div
                  className={`flex items-center space-x-2 ${step >= 3
                    ? "text-orange-600"
                    : "text-gray-400 dark:text-gray-500"
                    }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step >= 3 ? "border-orange-600 bg-orange-50 dark:bg-orange-900/20" : "border-current"
                      }`}
                  >
                    3
                  </div>
                  <span className="font-medium hidden sm:block">Details</span>
                </div>
              </div>
            )}

            {/* Content Brand Info */}    {/* Brand Selected Notice */}
            {selectedBrand && !isEmbedded && (
              <div className="generate-content-brand-notice min-w-0 mb-0 p-4 rounded-xl border bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-500/30">
                <div className="generate-content-brand-notice__layout min-w-0 gap-3">
                  <div className="generate-content-brand-notice__copy min-w-0 gap-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-400">
                      Brand Selected
                    </span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Content will be generated for:
                    </span>
                  </div>

                  <div className="generate-content-brand-notice__select relative w-full min-w-0" ref={dropdownRef}>
                    <div className="relative">
                      <button
                        type="button"
                        className="generate-content-brand-notice__trigger flex w-full min-w-0 items-center justify-between gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-[#fff] hover:bg-gray-50 dark:hover:bg-gray-800"
                        onClick={() => setShowBrandDropdown(!showBrandDropdown)}
                      >
                        <span className="min-w-0 flex-1 truncate overflow-hidden whitespace-nowrap dark:text-[#fff] text-left">
                          {brands?.find((b) => b._id === selectedBrand)
                            ?.brandName ||
                            selectedBrandName ||
                            "Select a brand"}
                        </span>
                        <ChevronDown className="h-5 w-5 flex-shrink-0" />
                      </button>

                      {showBrandDropdown && (
                        <div className="generate-content-brand-notice__menu absolute top-full left-0 right-0 mt-1 dark:text-[#fff] dark:bg-[#0A0A0A] dark:hover:bg-gray-800 bg-white border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-80 overflow-hidden">
                          {/* Search input */}
                          <div className="p-3 border-b border-gray-300 dark:border-gray-600 dark:text-[#fff]">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                              <input
                                placeholder="Search brands..."
                                value={brandSearchTerm}
                                onChange={(e) =>
                                  setBrandSearchTerm(e.target.value)
                                }
                                className="pl-10 h-9 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-[#fff] placeholder-gray-500 dark:placeholder-gray-400 focus:border-gray-400 dark:focus:border-gray-500 focus:ring-0 w-full"
                              />
                            </div>
                          </div>

                          {/* Brand list */}
                          <div className="max-h-60 overflow-y-auto dark:text-[#fff]">
                            {filteredBrands.length > 0 ? (
                              filteredBrands.map((brand, index) => (
                                <button
                                  key={index}
                                  onClick={() => {
                                    setSelectedBrand(brand._id);
                                    setLocalBrandName(brand.brandName);
                                    if (onHandleBrandSelect) {
                                      onHandleBrandSelect(brand._id);
                                    }
                                    setShowBrandDropdown(false);
                                  }}
                                  className="w-full min-w-0 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2 text-gray-900 dark:text-[#fff]"
                                >
                                  {selectedBrand === brand._id ? (
                                    <Check className="text-green-500 w-4 h-4" />
                                  ) : (
                                    <span className="w-4"></span>
                                  )}
                                  <span className="min-w-0 flex-1 truncate">{brand?.brandName}</span>
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-6 text-center text-gray-400 dark:text-gray-500">
                                <div className="text-sm">No brands found</div>
                                <div className="text-xs">
                                  Try adjusting your search
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Audience Pre-selected Notice */}
            {selectedAudienceId && selectedAudienceName && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 border border-blue-300 dark:border-blue-700 px-2 py-1 rounded text-xs font-medium">
                    Audience Selected
                  </span>
                  <span className="text-gray-700 dark:text-gray-300">
                    Content will be generated for{" "}
                    <strong>{selectedAudienceName}</strong>
                  </span>
                </div>
              </div>
            )}

            {/* Step 1: Content Type Selection */}
            {!isEmbedded && step >= 1 && (
              <div className="space-y-4">
                <div className="text-center">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-[#fff] mb-2">
                    What would you like to create?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Choose the type of content you want to generate
                  </p>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  {contentTypes.map((type) => {
                    const isSelectable = type.id === "ads" || type.id === "videos";
                    return (
                      <div
                        key={type.id}
                        className={`p-4 transition-all border-2 rounded-lg ${isSelectable
                          ? `cursor-pointer ${selectedContentType === type.id
                            ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                            : type.color
                          }`
                          : "opacity-50 cursor-not-allowed bg-gray-100 dark:bg-[#222222] border-gray-200 dark:border-gray-700"
                          }`}
                        onClick={
                          isSelectable
                            ? () => handleContentTypeSelect(type.id)
                            : undefined
                        }
                        tabIndex={isSelectable ? 0 : -1}
                        aria-disabled={!isSelectable}
                      >
                        <div className="text-center space-y-3">
                          <div
                            className={`w-10 h-10 mx-auto rounded-lg flex items-center justify-center ${isSelectable
                              ? selectedContentType === type.id
                                ? "bg-orange-500 text-white"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                              : "bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-500"
                              }`}
                          >
                            {getIconComponent(type.icon)}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-[#fff] text-sm">
                              {type.title}
                            </h4>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              {type.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Video Options - Only show when Videos is selected */}
                {selectedContentType === "videos" && step === 1 && (
                  <div className="mt-8 animate-in fade-in slide-in-from-top-4 duration-300">
                    <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                      Choose Video Type
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {videoCategories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => handleVideoCategorySelect(category)}
                          className="p-4 rounded-xl border border-gray-200 bg-white hover:border-orange-500 hover:bg-orange-50 transition-all text-left flex items-start gap-4 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10"
                        >
                          <div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0 dark:bg-orange-500/20 dark:text-orange-400">
                            {getIconComponent(category.icon)}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white">
                              {category.title}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {category.description}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Generate Section */}
                {selectedContentType && step === 1 && forceUpdate >= 0 && (
                  <div
                    key={`${selectedContentType}-${forceUpdate}`}
                    className="mt-6 border border-orange-200 dark:border-orange-500/30 rounded-lg bg-white dark:bg-white/5 shadow-sm"
                  >
                    {/* Header */}
                    <div className="px-4 py-3 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-500/10 dark:to-amber-500/10 border-b border-orange-100 dark:border-white/10 rounded-t-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-orange-500 flex items-center justify-center">
                          {getIconComponent("Zap")}
                        </div>
                        <span className="font-medium text-gray-900 dark:text-white text-sm">
                          Quick Generate
                        </span>
                        <span className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 px-2 py-0.5 rounded text-xs font-medium text-gray-700 dark:text-gray-300">
                          {
                            contentTypes.find(
                              (t) => t.id === selectedContentType
                            )?.title
                          }
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <div className="flex items-center justify-between gap-4">
                        {/* Left side - Quantity and Size */}
                        <div className="flex items-center gap-6">
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                              Quantity:
                            </span>
                            <div className="flex items-center border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800">
                              <button
                                type="button"
                                onClick={() =>
                                  setAdsCount(Math.max(1, adsCount - 1))
                                }
                                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-l-md"
                              >
                                {getIconComponent("Minus")}
                              </button>
                              <div className="px-3 py-1.5 bg-white dark:bg-white/10 border-x border-gray-200 dark:border-white/10 min-w-[40px] text-center text-sm font-medium text-gray-900 dark:text-white">
                                {adsCount}
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  setAdsCount(Math.min(10, adsCount + 1))
                                }
                                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-r-md"
                              >
                                {getIconComponent("Plus")}
                              </button>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                              Size:
                            </span>
                            <select
                              value={selectedSize}
                              onChange={(e) => setSelectedSize(e.target.value)}
                              className="w-[140px] h-9 border border-gray-300 dark:border-white/10 rounded-md px-3 text-sm bg-white dark:bg-white/5 text-gray-900 dark:text-white"
                            >
                              {sizeOptions.map((size) => (
                                <option key={size.id} value={size.id}>
                                  {size.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Right side - Actions */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setStep(2)}
                            className="border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm px-4 py-2 rounded-md text-gray-700 dark:text-gray-300"
                            disabled={isGenerating}
                          >
                            Add More Inputs
                          </button>
                          <button
                            onClick={handleGenerate}
                            disabled={isGenerating}
                            className="bg-orange-500 hover:bg-orange-600 text-white text-sm px-4 py-2 rounded-md font-medium flex items-center gap-2"
                          >
                            {getIconComponent("Zap")}
                            {isGenerating
                              ? "Generating..."
                              : `Generate ${contentTypes.find(
                                (t) => t.id === selectedContentType
                              )?.title
                              }`}
                          </button>
                        </div>
                      </div>

                      {/* Footer note */}
                      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                          Uses your brand profile and default audience settings
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}


            {/* Step 2: Audience Selection */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                Who is your target audience? (Optional)
              </h3>
              <p className="text-sm mb-4 text-gray-600 dark:text-gray-400">
                Selecting an audience helps create more targeted content, or skip to use your brand's default audience
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Add Custom Audience Card - First */}
                <button
                  className={`p-4 rounded-xl border-2 border-dashed text-left transition-all ${selectedAudience === "new"
                    ? "border-orange-500 bg-orange-50 dark:bg-orange-500/10"
                    : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100 dark:border-white/20 dark:bg-white/5 dark:hover:border-white/30 dark:hover:bg-white/10"
                    }`}
                  onClick={() => {
                    setEditingAudience(null);
                    setShowCreateModal(true);
                  }}
                >
                  <div className="flex flex-col items-center justify-center h-full min-h-[80px]">
                    <div className="h-6 w-6 mb-2 flex items-center justify-center text-gray-600 dark:text-gray-400">
                      {getIconComponent("Plus")}
                    </div>
                    <span
                      className="text-sm font-semibold text-gray-700 dark:text-gray-300"
                    >
                      Create New Audience
                    </span>
                    <span className="text-xs mt-1 text-gray-500">
                      Define your own target audience
                    </span>
                  </div>
                </button>
                {/* API Audiences */}
                {audiences.length === 0 && (
                  <div className="col-span-full text-center text-gray-400 dark:text-gray-500 py-8">
                    No audiences found.
                  </div>
                )}
                {allAudiences.map((audience) => (
                  <button
                    key={audience._id || audience.id}
                    onClick={() => {
                      const audienceId = audience._id || audience.id;
                      if (selectedAudience === audienceId) {
                        setSelectedAudience("");
                      } else {
                        setSelectedAudience(audienceId);
                        setStep(3);
                      }
                    }}
                    className={`p-4 rounded-xl border text-left transition-all ${selectedAudience === (audience._id || audience.id)
                      ? "border-orange-500 bg-orange-50 dark:bg-orange-500/10"
                      : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-white/10 dark:bg-white/5 dark:hover:border-white/20 dark:hover:bg-white/10"
                      }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${selectedAudience === audience._id
                          ? "bg-orange-500 text-white"
                          : "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-400"
                          }`}
                      >
                        {getIconComponent(audience.icon || "Users")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <div
                            className="font-semibold truncate text-gray-900 dark:text-white"
                          >
                            {audience.name || audience.title}
                          </div>
                          {audience.isCustom && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider bg-gray-200 text-gray-600 dark:bg-white/10 dark:text-gray-300">
                              Custom
                            </span>
                          )}
                        </div>
                        <div className="text-xs line-clamp-2 text-gray-600 dark:text-gray-400">
                          {audience.description}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 3: Details and Generation */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                Tell us about your content
              </h3>
              <p className="text-sm mb-4 text-gray-600 dark:text-gray-400">
                Provide additional details to customize your content, or let our AI create great content with your brand profile
              </p>

              <div className="mb-4">
                <label className="text-sm font-medium mb-2 block text-gray-900 dark:text-white">
                  Describe what you want to create (Optional)
                </label>
                <textarea
                  placeholder="Add specific details, key benefits, or goals (optional - we can generate great content with just your brand and audience selection)"
                  className="w-full min-h-[100px] rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-orange-500 bg-white border border-gray-200 text-gray-900 placeholder:text-gray-400 dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder:text-gray-500"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
              </div>

              {/* Quantity and Size Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                {/* Quantity */}
                <div>
                  <label className="text-sm font-medium mb-3 block text-gray-900 dark:text-white">
                    How many ads do you want to generate?
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center border rounded-xl overflow-hidden border-gray-200 bg-white dark:border-white/10 dark:bg-white/5">
                      <button
                        type="button"
                        onClick={() => setAdsCount(Math.max(1, adsCount - 1))}
                        disabled={adsCount <= 1}
                        className="h-10 w-10 flex items-center justify-center text-gray-900 hover:bg-gray-100 disabled:text-gray-300 dark:text-white dark:hover:bg-white/10 dark:disabled:text-gray-600"
                      >
                        <div className="w-4 h-4">{getIconComponent("Minus")}</div>
                      </button>
                      <input
                        type="number"
                        value={adsCount}
                        onChange={(e) => setAdsCount(Math.min(10, Math.max(1, Number.parseInt(e.target.value) || 1)))}
                        className="w-12 text-center font-semibold bg-transparent border-0 focus:outline-none focus:ring-0 text-gray-900 dark:text-white"
                        min="1"
                        max="10"
                      />
                      <button
                        type="button"
                        onClick={() => setAdsCount(Math.min(10, adsCount + 1))}
                        disabled={adsCount >= 10}
                        className="h-10 w-10 flex items-center justify-center text-gray-900 hover:bg-gray-100 disabled:text-gray-300 dark:text-white dark:hover:bg-white/10 dark:disabled:text-gray-600"
                      >
                        <div className="w-4 h-4">{getIconComponent("Plus")}</div>
                      </button>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      ads (1-10)
                    </span>
                  </div>
                  <p className="text-xs mt-2 text-gray-500">
                    More ads will give you more variety and options to choose from
                  </p>
                </div>
                {/* Size */}
                <div>
                  <label className="text-sm font-medium mb-3 block text-gray-900 dark:text-white">
                    What size do you want?
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="w-full justify-between rounded-xl h-10 px-3 border border-gray-200 bg-white text-gray-900 hover:bg-gray-50 dark:bg-white/5 dark:border-white/10 dark:text-white dark:hover:bg-white/10 inline-flex items-center text-sm font-medium"
                    >
                      <span className="flex items-center gap-2">
                        <AspectRatioIcon ratio={selectedSize} />
                        {aspectRatios.find((r) => r.id === selectedSize)?.label}
                      </span>
                      <ChevronDown className="h-4 w-4 shrink-0" />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 rounded-md border border-gray-200 dark:border-white/10 bg-white dark:bg-[#111111] shadow-lg z-10 p-1">
                        {aspectRatios.map((ratio) => (
                          <button
                            key={ratio.value}
                            type="button"
                            onClick={() => handleAspectRatioChange(ratio.id)}
                            className="w-full px-2 py-1.5 text-left rounded-sm flex items-center gap-2 text-sm select-none text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-white/5"
                          >
                            <AspectRatioIcon ratio={ratio.id} />
                            {ratio.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Choose the aspect ratio for your content
                  </p>
                </div>
              </div>

              {/* Enhanced Reference Image Upload with Drag & Drop and Paste */}
              <div className="mb-8">
                <label className="text-sm font-medium mb-3 block text-gray-900 dark:text-white">
                  Reference Image (optional)
                </label>
                <p className="text-sm mb-4 text-gray-600 dark:text-gray-400">
                  Upload, drag & drop, or paste an image to inspire the visual style.
                </p>
                <div
                  ref={dropZoneRef}
                  className={`w-full p-8 rounded-xl border-2 border-dashed transition-all ${isDragOver
                    ? "border-orange-500 bg-orange-50 dark:bg-orange-500/10"
                    : referenceImage
                      ? "border-green-500 bg-green-50 dark:bg-green-500/10"
                      : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100 dark:border-white/20 dark:bg-white/5 dark:hover:border-white/30 dark:hover:bg-white/10"
                    }`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="reference-image"
                  />
                  <label htmlFor="reference-image" className="cursor-pointer block w-full">
                    <div className="flex flex-col items-center justify-center">
                      {referenceImage ? (
                        <>
                          <div className="w-8 h-8 mb-3 flex items-center justify-center text-green-600 dark:text-green-400">
                            {getIconComponent("Upload")}
                          </div>
                          <span className="text-sm font-medium mb-1 text-green-600 dark:text-green-400">
                            Image uploaded: {referenceImage.name}
                          </span>
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            (Click to change)
                          </span>
                        </>
                      ) : isDragOver ? (
                        <>
                          <div className="w-8 h-8 mb-3 animate-bounce flex items-center justify-center text-orange-600 dark:text-orange-400">
                            {getIconComponent("Upload")}
                          </div>
                          <span className="text-sm font-medium mb-1 text-orange-600 dark:text-orange-400">
                            Drop your image here
                          </span>
                          <span className="text-xs text-orange-500 dark:text-orange-400">
                            Release to upload
                          </span>
                        </>
                      ) : (
                        <>
                          <div className="w-8 h-8 mb-3 flex items-center justify-center text-gray-400">
                            {getIconComponent("Upload")}
                          </div>
                          <span className="text-sm font-medium mb-1 text-gray-900 dark:text-white">
                            Add an image
                          </span>
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            Click to browse, drag & drop, or paste (Ctrl+V)
                          </span>
                          <span className="text-xs mt-2 text-gray-500">
                            Supports JPG, PNG, GIF up to 10MB
                          </span>
                        </>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              {/* Summary Section */}
              <div
                className="p-4 rounded-xl border bg-gray-50 border-gray-200 dark:bg-white/5 dark:border-white/10"
              >
                <h4 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">
                  Your Selection:
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium min-w-[100px] text-gray-600 dark:text-gray-400">
                      Content Type:
                    </span>
                    <div className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-white">
                      {contentTypes.find((t) => t.id === selectedContentType)?.title || "—"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium min-w-[100px] text-gray-600 dark:text-gray-400">
                      Audience:
                    </span>
                    <div className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-white">
                      {allAudiences.find((a) => a._id === selectedAudience)?.name ||
                        allAudiences.find((a) => a._id === selectedAudience)?.title || "—"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium min-w-[100px] text-gray-600 dark:text-gray-400">
                      Brand:
                    </span>
                    <div className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-white">
                      {localBrandName || "No Brand Selected"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium min-w-[100px] text-gray-600 dark:text-gray-400">
                      Quantity:
                    </span>
                    <div className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-white">
                      {adsCount} ads
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium min-w-[100px] text-gray-600 dark:text-gray-400">
                      Size:
                    </span>
                    <div className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-white">
                      {sizeOptions.find((s) => s.id === selectedSize)?.label || "—"}
                    </div>
                  </div>
                  {referenceImage && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium min-w-[100px] text-gray-600 dark:text-gray-400">
                        Reference Image:
                      </span>
                      <div className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400">
                        {referenceImage.name}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {referenceCard && (
              <div className="flex items-center justify-center">
                <div className="mt-6 w-[340px]">
                  <div className="text-sm font-medium text-gray-900 dark:text-[#fff] mb-3">
                    Reference:
                  </div>
                  <div className="space-y-2">
                    <div
                      className="w-full p-3 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 transition-colors cursor-pointer"
                      onClick={() => setShowOriginalReference((prev) => !prev)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-[#fff]">
                            Original Ad Reference
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            {showOriginalReference
                              ? "Click to hide reference"
                              : "Click to view original ad used for inspiration"}
                          </div>
                        </div>
                        <svg
                          className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform ${showOriginalReference ? "rotate-180" : ""
                            }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                    {showOriginalReference && (
                      <div className="w-full p-4 border border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                        <div
                          className="mb-3 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden shadow-md flex justify-center"
                        >
                          <img
                            src={referenceCard?.content?.imageUrl[0]?.url}
                            alt="Original Ad Reference"
                            className="w-full h-auto object-contain max-h-[500px]"
                          />
                        </div>
                        <div className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                          <RichDescription
                            descriptionHtml={referenceCard?.aiData?.descriptionHtml || referenceCard?.content?.extractedData?.descriptionHtml}
                            description={referenceCard?.aiData?.description || referenceCard?.adData?.description || referenceCard?.content?.extractedData?.description || extractReferenceText(referenceCard?.content?.html)}
                            descriptionLineHeight={referenceCard?.aiData?.descriptionLineHeight || referenceCard?.content?.extractedData?.descriptionLineHeight}
                            maxWords={20}
                            expanded={isReferenceExpanded}
                            onToggle={setIsReferenceExpanded}
                            toggleClassName="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 ml-2 font-medium"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons - Only show when not using Quick Generate */}
            {!(selectedContentType && step === 1) && (
              <div className="flex gap-3 justify-end mt-8 pt-6 border-t border-gray-200 dark:border-white/10">
                {/* <button
                  className="px-6 py-2.5 rounded-xl font-medium transition-all bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                  onClick={() => {
                    if (isEmbedded && step === 2) {
                      if (onBackToContentTypes) onBackToContentTypes();
                    } else {
                      setStep(step > 2 ? step - 1 : 1);
                    }
                  }}
                  disabled={isGenerating}
                >
                  {isEmbedded && step === 2 ? "Back to Content Types" : (step === 3 ? "Back to Audience" : "Back")}
                </button> */}
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="px-6 py-2.5 rounded-xl font-medium text-white transition-all bg-orange-500 hover:bg-orange-600 disabled:opacity-50"
                >
                  {isGenerating
                    ? "Generating Content..."
                    : `Generate ${contentTypes.find((t) => t.id === selectedContentType)
                      ?.title || "Content"
                    }`}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <CreateCampaignModal
        open={showCreateCampaingModal}
        onOpenChange={setShowCreateCampaingModal}
        prefilledData={true}
      // onSave={handleSaveCampaign}
      // editingCampaign={editingCampaign}
      />

      <CreateAudienceModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSave={handleSaveAudience}
        editingAudience={editingAudience}
      />

      {/* Custom Audience Modal would go here */}
      {showCustomAudienceModal && (
        <CustomAudienceModal
          open={showCustomAudienceModal}
          onOpenChange={setShowCustomAudienceModal}
          onSave={handleSaveCustomAudience}
        />
      )}
    </>
  );
};

export default GenerateContentModal;
